import { apiClient } from './configs/axios-setup'

export type ReportStatus = 'pending' | 'processing' | 'completed' | 'failed'

/** A referenced image or GIF to embed in the report. */
export interface ReportMediaRef {
  /** Full (SAS) URL of the source media in Azure Storage. */
  url: string
  caption?: string
  /** Rendered as a video when 'video', otherwise an image. */
  type?: 'image' | 'video'
  /** Durable blob path of the report's own copy (server refreshes the URL on read). */
  blobPath?: string
  /**
   * Source blob path ({gmid}/{runName}/{fileName}) and gmid for geometry-change
   * images. These live in the cfd-data container, which the browser cannot reach
   * directly, so previews must proxy the bytes through the server. `url` is still
   * the source SAS URL used by the server to copy the image on generation.
   */
  geomBlobPath?: string
  geomGmid?: string
}

/** A MAV-uploaded media item on a report. */
export interface ReportMavMediaItem {
  blobPath: string
  name: string
  type: 'image' | 'video'
  caption?: string
  timeAdded: number
  url?: string
}

/** Case metadata shown in the report header / delta table. */
export interface ReportCaseMeta {
  systemRunId?: string
  caseName: string
  projectName?: string
  runName?: string
  gm_id?: string
  ahf?: number
  ahr?: number
  yaw?: number
  steer?: number
  roll?: number
  speed?: number
}

/** A single row of headline delta metrics for a case. */
export interface ReportDeltaMetric {
  caseName: string
  gm_id?: string
  ref_gm_id?: string
  delta_cd?: number
  delta_cl?: number
  delta_clf?: number
  delta_clr?: number
  delta_csf?: number
  delta_csr?: number
  delta_pct_clf?: number
  /** Emphasize this row in the report's Delta table. */
  highlight?: boolean
}

/** A content section: comments, a recommendation and attached media. */
export interface ReportSectionInput {
  heading: string
  comments?: string[]
  recommendation?: string
  images?: ReportMediaRef[]
  gifs?: ReportMediaRef[]
  /** Anchor target so a summary's "Reports Included" list can scroll to this section. */
  anchorId?: string
  /** Per-report delta rows rendered as a table inside the section (summary decks). */
  deltaMetrics?: ReportDeltaMetric[]
}

/** The full JSON payload submitted to generate a report. */
export interface ReportPayload {
  title: string
  subtitle?: string
  author?: string
  cases?: ReportCaseMeta[]
  deltaMetrics?: ReportDeltaMetric[]
  sections?: ReportSectionInput[]
  /** External media pushed by MAV; rendered as the top "MAV" section. */
  mavMedia?: ReportMediaRef[]
  /** Notes pushed by MAV, shown as comments in the "MAV" section. */
  mavNotes?: string[]
  /** Titles of the source reports consolidated into a weekly summary report. */
  summaryReportNames?: string[]
}

/** A single case of a selected run, handed off from Headline Data. */
export interface ReportRunCase {
  gmId: string
  systemRunId: string
  stateName?: string
  refGmId?: string
  yaw?: number
  steer?: number
  roll?: number
  speed?: number
  delta_cd?: number
  delta_cl?: number
  delta_clf?: number
  delta_clr?: number
  delta_csf?: number
  delta_csr?: number
  delta_pct_clf?: number
}

/** A run selection (project + run + its cases) used to preload the Report Generator. */
export interface ReportRunSelection {
  project: string
  runName: string
  cases: ReportRunCase[]
  /** Optional run media to preload as a section (from the Run Media tab). */
  media?: ReportMediaRef[]
  /** Marked-up images to preload as a dedicated "Mark-Up" section. */
  markupMedia?: ReportMediaRef[]
  /** Changes each time so the Report Generator reloads even for the same run. */
  token: number
}

export interface ReportJob {
  reportId: string
  raceTeamId: string
  status: ReportStatus
  title: string
  payload: ReportPayload
  resultBlobPath?: string
  resultUrl?: string
  sizeBytes?: number
  mediaCount?: number
  error?: string
  mavCaseKey?: string
  mavMedia?: ReportMavMediaItem[]
  mavNotes?: string[]
  createByUser: string
  createByUserName: string
  timeCreated: number
  timeUpdated: number
  timeCompleted?: number
}

/** Create an HTML report generation job. Returns the pending job. */
export const createReport = (payload: ReportPayload): Promise<ReportJob> => {
  return apiClient.post('/GeneratedReports', payload).then(res => res.data.job as ReportJob)
}

/** Build report HTML from a payload without storing it. Returns the HTML string. */
export const previewReport = (payload: ReportPayload, inline = false): Promise<string> => {
  const query = inline ? '?inline=1' : ''
  return apiClient
    .post(`/GeneratedReports/preview${query}`, payload)
    .then(res => res.data.html as string)
}

/** Poll a report generation job's status. */
export const getReportJob = (reportId: string): Promise<ReportJob> => {
  return apiClient
    .get(`/GeneratedReports/${encodeURIComponent(reportId)}`)
    .then(res => res.data.job as ReportJob)
}

/** List report generation jobs for the current team. */
export const listReports = (limit = 50): Promise<ReportJob[]> => {
  return apiClient.get(`/GeneratedReports?limit=${limit}`).then(res => res.data.jobs as ReportJob[])
}

/** Update an existing report and rebuild it. Returns the updated job. */
export const updateReport = (reportId: string, payload: ReportPayload): Promise<ReportJob> => {
  return apiClient
    .put(`/GeneratedReports/${encodeURIComponent(reportId)}`, payload)
    .then(res => res.data.job as ReportJob)
}

/** Delete a report (only the creator may delete). */
export const deleteReport = (reportId: string): Promise<void> => {
  return apiClient.delete(`/GeneratedReports/${encodeURIComponent(reportId)}`).then(() => undefined)
}

/** Upload local files and get media refs to attach to a report section. */
export const uploadSectionMedia = (files: File[]): Promise<ReportMediaRef[]> => {
  const form = new FormData()
  files.forEach(f => form.append('files', f))
  return apiClient
    .post('/GeneratedReports/upload-media', form, {
      // apiClient defaults to application/json, which makes axios JSON-ify the
      // FormData and drop the files. Strip the header so the browser sets
      // multipart/form-data + boundary, and pass the FormData through untouched.
      transformRequest: [
        (data, headers) => {
          headers.delete('Content-Type')
          return data
        }
      ]
    })
    .then(res => res.data.media as ReportMediaRef[])
}

/** A geometry-change image attached to a run, resolved to a downloadable URL. */
export interface ReportGeometryImage {
  url: string
  name: string
  /**
   * Blob path ({gmid}/{runName}/{fileName}) and gmid for fetching this image via
   * the DownloadBlobProxy. Geometry images live in the cfd-data container, which
   * is not reachable directly from the browser, so display them by proxying the
   * bytes through the server rather than putting `url` in an <img> tag.
   */
  blobPath?: string
  gmid?: string
}

/** Resolve the geometry-change images attached to a run into downloadable URLs. */
export const getRunGeometryImages = (runName: string): Promise<ReportGeometryImage[]> => {
  return apiClient
    .get(`/GeneratedReports/geometry-images?runName=${encodeURIComponent(runName)}`)
    .then(res => res.data.images as ReportGeometryImage[])
}

/** A MAV-pushed media item resolved for a case. */
export interface MavMediaForCase {
  url: string
  name: string
  type: 'image' | 'video'
  caption?: string
  caseKey: string
}

/** Resolve MAV-pushed media for a set of case keys into downloadable URLs. */
export const getMavMediaForCases = (caseKeys: string[]): Promise<MavMediaForCase[]> => {
  const q = encodeURIComponent(caseKeys.join(','))
  return apiClient
    .get(`/GeneratedReports/mav-media?caseKeys=${q}`)
    .then(res => res.data.media as MavMediaForCase[])
}

/** Build a PowerPoint (.pptx) deck from a payload. Returns the file as a Blob. */
export const exportReportPptx = (payload: ReportPayload): Promise<Blob> => {
  return apiClient.post('/GeneratedReports/pptx', payload).then(res => {
    const byteChars = atob(res.data.base64 as string)
    const bytes = new Uint8Array(byteChars.length)
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i)
    return new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    })
  })
}
